import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import type { AGUSegmentOption } from 'agu_app_shell';
import { MJConfirmService } from '@memberjunction/ng-ui-components';
import { IsPhaseAtLeast, OEP_ENTITY, OEP_LIVE_FILTER, type OEPDraftOrSubmitted } from 'agu_oep_core';
import type { FeedbackPlatformCommitteeSelectionEntity, FeedbackPlatformCommitteeSelectionItemEntity } from 'mj_generatedentities';
import { OEPDataService, type OEPRowsQuery } from '../../data/oep-data.service';
import { OEPFilesService } from '../../data/oep-files.service';
import { OEPViewBase } from '../../state/oep-view-base';
import type { OEPStatusLine } from '../shared/status';
import { OEP_TAB_BLOCKED, OEPOpenInNewTab } from '../shared/open-tab';
import { OEPLatestFiles, OEPPresentationLinkSql, OEPUploadedFilesSql } from '../shared/files';
import { DateOrNull, NumOrNull, Str, StrOrNull, Up, type OEPRow } from '../shared/rows';
import {
    BuildCommitteeVM, CanEditSlate, CanSubmitSlate, GroupNominees,
    type CommitteeVM, type OEPCommNominationInput, type OEPCommNominee, type OEPCommNomineeVM, type OEPCommPresentationInput,
    type OEPCommReviewInput, type OEPCommScoreInput, type OEPCommSelectionInput,
} from './committee.vm';
import { BuildScoreRows, DownloadScores, ScoreFileName } from './committee-export';

/** Implementer copy (T28 fix round 1): shown after a first pick lost the race to create the section's slate. */
export const OEP_SLATE_RACE = 'Your committee\'s slate was just started by another member — select again.';
const asStatus = (v: string | null): OEPDraftOrSubmitted | null => (v === 'Draft' || v === 'Submitted' ? v : null);
/** Review form questions 6 and 7 (metadata/review-questions/oep-review-form.json): "What worked" and the suggestions. */
const STRENGTHS_SEQUENCE = 6;
const SUGGESTIONS_SEQUENCE = 7;

/**
 * Section committee ("Select {Section} winners") — markup.html:762-841. All copy and rules live in
 * committee.vm.ts (pure, spec'd). Load gathers the packets of every nominee in ONE RunViews batch
 * (OEPDataService.Batch; RLS, T5: the committee reads its section's presentations, Submitted
 * nominations and their responses from Selecting on, released reviews and OEP Final files) and joins
 * on the client — no per-nominee query. Before Selecting only the member's own Committee Members row
 * is read (committees cannot read nominations yet).
 *
 * Writes go through OEPViewBase.Act, and the enabling rules mirror agu_oep_core (decisions.ts):
 * picks and unpicks only while the Selection window is open and the slate is a Draft
 * (DecideSelectionItemAdd / Remove), at most WinnersPerSection, and Submit only with ≥1 pick
 * (DecideSelectionChange), stamping SubmittedByUserID = me (a self-stamp column). The first pick
 * creates the section's Committee Selections row (SeasonID + SectionID). A pick sends Slot = 1; the
 * server assigns the lowest free slot, and a race for the last slot fails one insert on
 * UQ_CommitteeSelectionItem_Selection_Slot — that member sees the server message (toast + aria-live)
 * and the view reloads. Submit asks MJConfirmService.Confirm first. The score sheet is built with
 * ExportEngine.toExcel (committee-export.ts).
 */
@Component({
    standalone: false,
    selector: 'oep-committee',
    templateUrl: './oep-committee.component.html',
    styleUrl: './oep-committee.component.scss',
})
export class OEPCommitteeComponent extends OEPViewBase implements OnChanges {
    private readonly confirm = inject(MJConfirmService);
    private readonly files = inject(OEPFilesService);

    VM: CommitteeVM | null = null;
    /** I have no Committee Members row in the active season. */
    NoCommittee = false;
    /** The result of the last pick, submit, download or file open, announced in the aria-live line. */
    Status: OEPStatusLine | null = null;

    /** The chosen section (the `committee` location's record ID); null = my first section. */
    @Input() SectionID: string | null = null;
    /** My sections this season, by name; the switcher shows when there is more than one. */
    Sections: AGUSegmentOption[] = [];

    private readonly expanded = new Set<string>();
    private slotsUsed = 0;
    private initialized = false;
    private exported = false;
    private seasonID: string | null = null;
    private sectionID: string | null = null;
    private sectionName = '';
    private seasonCode = '';
    private nominees: OEPCommNominee[] = [];
    private selection: OEPCommSelectionInput | null = null;
    /** Inputs the VM is rebuilt from when only view state (expand, exported) changes. */
    private counts = { Nominations: 0, Presentations: 0 };

    /**
     * Phase 1 reads my Committee Members rows for the active season (ordered by section name). A member
     * of two sections' committees (UQ_CommitteeMember_Season_Section_User allows it) picks one with the
     * section switcher; the choice is the `committee` location's record ID, so Back and refresh keep it.
     * The requested ID is honored only when it is one of my rows (else the first row). Phase 2 scopes
     * EVERY query to that one validated SectionID.
     */
    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        const me = OEPDataService.G(this.Context.Me.UserID);
        const sid = OEPDataService.G(season.ID);
        this.seasonID = sid;
        this.seasonCode = season.Code;
        const [committee] = await this.Data.Batch([
            { EntityName: OEP_ENTITY.CommitteeMembers, ExtraFilter: `SeasonID='${sid}' AND UserID='${me}'`, Fields: ['SectionID', 'Section'], OrderBy: 'Section, SectionID' },
        ]);
        this.Sections = committee.map((m) => ({ Key: Str(m, 'SectionID'), Label: Str(m, 'Section') }));
        const wanted = this.SectionID;
        const member = committee.find((m) => wanted !== null && Up(Str(m, 'SectionID')) === Up(wanted)) ?? committee[0];
        this.NoCommittee = member === undefined;
        if (!member) {
            this.VM = null;
            return;
        }
        const sec = OEPDataService.G(Str(member, 'SectionID'));
        if (this.sectionID === null || Up(this.sectionID) !== Up(sec)) {
            // Another section: nothing of the previous one's view state carries over.
            this.expanded.clear();
            this.exported = false;
        }
        this.sectionID = sec;
        this.sectionName = Str(member, 'Section');
        this.nominees = [];
        this.selection = null;
        this.slotsUsed = 0;
        this.counts = { Nominations: 0, Presentations: 0 };
        if (!IsPhaseAtLeast(season.Phase, 'Selecting')) {
            this.rebuild(); // locked: committees cannot read nominations before Selecting (T5)
            return;
        }
        const secPres = `SELECT ID FROM FeedbackPlatform.Presentation WHERE SeasonID='${sid}' AND ${OEP_LIVE_FILTER.Presentation} AND SectionID='${sec}'`;
        const submitted = `Status='Submitted' AND SeasonID='${sid}' AND PresentationID IN (${secPres})`;
        const nomPres = `SELECT PresentationID FROM FeedbackPlatform.Nomination WHERE ${submitted}`;
        const released = `SELECT r.ID FROM FeedbackPlatform.Review r JOIN FeedbackPlatform.ReviewAssignment ra ON ra.ID = r.ReviewAssignmentID WHERE r.Status = 'Completed' AND ra.PresentationID IN (${nomPres})`;
        const forms = `FormType IN ('Nomination','Review') AND SeasonID IS NULL`;
        const mySlate = `SeasonID='${sid}' AND SectionID='${sec}'`;
        const queries: OEPRowsQuery[] = [
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: `ID IN (${secPres})`, Fields: ['ID', 'Title', 'Format', 'PresentationCode', 'PresenterUserID', 'PresenterUser'], OrderBy: 'PresentationCode, Title, ID' },
            // Presenters of nominated presentations and the nominators of Submitted nominations (both readable by the committee, T5).
            { EntityName: OEP_ENTITY.Participants, ExtraFilter: `SeasonID='${sid}' AND (UserID IN (SELECT PresenterUserID FROM FeedbackPlatform.Presentation WHERE ID IN (${nomPres})) OR UserID IN (SELECT NominatorUserID FROM FeedbackPlatform.Nomination WHERE ${submitted}))`, Fields: ['UserID', 'Institution', 'CareerStage'] },
            { EntityName: OEP_ENTITY.Nominations, ExtraFilter: submitted, Fields: ['ID', 'PresentationID', 'NominatorUserID', 'NominatorUser', 'AdditionalReasoning'], OrderBy: 'SubmittedAt, ID' },
            { EntityName: OEP_ENTITY.NominationResponses, ExtraFilter: `NominationID IN (SELECT ID FROM FeedbackPlatform.Nomination WHERE ${submitted})`, Fields: ['NominationID', 'ReviewQuestionID', 'NumericValue', 'TextValue'] },
            { EntityName: OEP_ENTITY.ReviewQuestions, ExtraFilter: forms, Fields: ['ID', 'Sequence'] },
            { EntityName: OEP_ENTITY.ReviewQuestionRubrics, ExtraFilter: `ReviewQuestionID IN (SELECT ID FROM FeedbackPlatform.ReviewQuestion WHERE ${forms})`, Fields: ['ID', 'ReviewQuestionID', 'RatingValue', 'RatingLabel'] },
            { EntityName: OEP_ENTITY.ReviewAssignments, ExtraFilter: `PresentationID IN (${nomPres})`, Fields: ['ID', 'PresentationID'] },
            { EntityName: OEP_ENTITY.Reviews, ExtraFilter: `ID IN (${released})`, Fields: ['ID', 'ReviewAssignmentID'], OrderBy: 'CompletedDate, ID' },
            { EntityName: OEP_ENTITY.ReviewResponses, ExtraFilter: `ReviewID IN (${released})`, Fields: ['ReviewID', 'ReviewQuestionID', 'NumericValue', 'TextValue', 'RubricLevelID'] },
            { EntityName: OEP_ENTITY.Files, ExtraFilter: OEPUploadedFilesSql(nomPres, ['Final']), Fields: ['ID', 'Name', 'Category', 'Status', 'ContentType', '__mj_CreatedAt'], OrderBy: '__mj_CreatedAt DESC' },
            { EntityName: OEP_ENTITY.FileEntityRecordLinks, ExtraFilter: OEPPresentationLinkSql(nomPres), Fields: ['FileID', 'RecordID'] },
            { EntityName: OEP_ENTITY.CommitteeSelections, ExtraFilter: mySlate, Fields: ['ID', 'Status', 'SubmittedAt'] },
            { EntityName: OEP_ENTITY.CommitteeSelectionItems, ExtraFilter: `CommitteeSelectionID IN (SELECT ID FROM FeedbackPlatform.CommitteeSelection WHERE ${mySlate})`, Fields: ['ID', 'PresentationID'] },
        ];
        const [presentations, participants, nominations, nomResponses, questions, rubrics,
            assignments, reviews, reviewResponses, fileRows, links, selections, items] = await this.Data.Batch(queries);

        const slate = selections[0];
        const status = slate ? asStatus(StrOrNull(slate, 'Status')) : null;
        this.selection = slate && status ? { ID: Str(slate, 'ID'), Status: status, SubmittedAt: DateOrNull(slate, 'SubmittedAt') } : null;
        this.slotsUsed = items.length;

        const seqOf = new Map(questions.map((q) => [Up(Str(q, 'ID')), Number(q['Sequence'])]));
        const rubricByID = new Map(rubrics.map((r) => [Up(Str(r, 'ID')), r]));
        const rubricFor = (questionID: string, value: number | null): OEPRow | undefined =>
            value === null ? undefined : rubrics.find((r) => Up(Str(r, 'ReviewQuestionID')) === Up(questionID) && Number(r['RatingValue']) === value);
        const score = (x: OEPRow): OEPCommScoreInput[] => {
            const seq = seqOf.get(Up(Str(x, 'ReviewQuestionID')));
            if (seq === undefined || seq < 1 || seq > 5) return [];
            const level = StrOrNull(x, 'RubricLevelID');
            const rubric = (level ? rubricByID.get(Up(level)) : undefined) ?? rubricFor(Str(x, 'ReviewQuestionID'), NumOrNull(x, 'NumericValue'));
            return [{
                Sequence: seq,
                Level: rubric ? Number(rubric['RatingValue']) : NumOrNull(x, 'NumericValue'),
                LevelLabel: rubric ? Str(rubric, 'RatingLabel') : '',
                Text: StrOrNull(x, 'TextValue') ?? '',
            }];
        };
        const byKey = (rows: readonly OEPRow[], key: string): Map<string, OEPRow[]> => {
            const m = new Map<string, OEPRow[]>();
            for (const r of rows) {
                const k = Up(Str(r, key));
                m.set(k, [...(m.get(k) ?? []), r]);
            }
            return m;
        };
        const participantOf = new Map(participants.map((x) => [Up(Str(x, 'UserID')), x]));
        const nomResponsesOf = byKey(nomResponses, 'NominationID');
        const reviewResponsesOf = byKey(reviewResponses, 'ReviewID');
        const presentationOfAssignment = new Map(assignments.map((a) => [Up(Str(a, 'ID')), Str(a, 'PresentationID')]));
        const finalOf = OEPLatestFiles(fileRows, links, 'Final');

        const presInputs: OEPCommPresentationInput[] = presentations.map((p) => {
            const who = participantOf.get(Up(Str(p, 'PresenterUserID')));
            return {
                PresentationID: Str(p, 'ID'), Title: Str(p, 'Title'), PresenterName: Str(p, 'PresenterUser'),
                Institution: who ? StrOrNull(who, 'Institution') : null,
                CareerStage: who ? StrOrNull(who, 'CareerStage') : null,
                Format: StrOrNull(p, 'Format'), Code: StrOrNull(p, 'PresentationCode'),
                FinalFileID: finalOf.get(Up(Str(p, 'ID')))?.FileID ?? null,
            };
        });
        const nomInputs: OEPCommNominationInput[] = nominations.map((n) => {
            const who = participantOf.get(Up(Str(n, 'NominatorUserID')));
            return {
                NominationID: Str(n, 'ID'), PresentationID: Str(n, 'PresentationID'), NominatorName: Str(n, 'NominatorUser'),
                NominatorInstitution: who ? StrOrNull(who, 'Institution') : null,
                Scores: (nomResponsesOf.get(Up(Str(n, 'ID'))) ?? []).flatMap(score),
                AdditionalReasoning: StrOrNull(n, 'AdditionalReasoning'),
            };
        });
        const reviewInputs: OEPCommReviewInput[] = reviews.flatMap((r) => {
            const pid = presentationOfAssignment.get(Up(Str(r, 'ReviewAssignmentID')));
            if (pid === undefined) return [];
            const rs = reviewResponsesOf.get(Up(Str(r, 'ID'))) ?? [];
            const text = (seq: number): string => {
                const x = rs.find((row) => seqOf.get(Up(Str(row, 'ReviewQuestionID'))) === seq);
                return x ? (StrOrNull(x, 'TextValue') ?? '') : '';
            };
            return [{ ReviewID: Str(r, 'ID'), PresentationID: pid, Scores: rs.flatMap(score), Strengths: text(STRENGTHS_SEQUENCE), Suggestions: text(SUGGESTIONS_SEQUENCE) }];
        });
        this.nominees = GroupNominees(presInputs, nomInputs, reviewInputs, items.map((i) => ({ ID: Str(i, 'ID'), PresentationID: Str(i, 'PresentationID') })));
        this.counts = { Nominations: nominations.length, Presentations: presentations.length };
        this.rebuild();
    }

    /** The section on screen (the switcher's pressed option). */
    get CurrentSectionID(): string {
        return this.sectionID === null ? '' : (this.Sections.find((s) => Up(s.Key) === Up(this.sectionID ?? ''))?.Key ?? '');
    }

    override ngOnInit(): void {
        this.initialized = true;
        super.ngOnInit();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized || !changes['SectionID'] || changes['SectionID'].isFirstChange()) return;
        this.Status = null;
        void this.Refresh();
    }

    /** The section switcher: navigates to `committee` with that Section ID (Back/refresh keep it); ngOnChanges reloads. */
    ChooseSection(sectionID: string): void {
        if (this.Busy || (this.sectionID !== null && Up(sectionID) === Up(this.sectionID))) return;
        if (!this.Sections.some((s) => Up(s.Key) === Up(sectionID))) return;
        this.Go('committee', sectionID);
    }

    private rebuild(): void {
        const season = this.Context.Season;
        this.VM = season ? BuildCommitteeVM({
            Phase: season.Phase, Timeline: season.Timeline, SeasonCode: season.Code, Cap: season.WinnersPerSection,
            SectionName: this.sectionName, Now: new Date(), Resources: this.Context.Resources.committee ?? [],
            NominationCount: this.counts.Nominations, PresentationCount: this.counts.Presentations,
            Nominees: this.nominees, SlotsUsed: this.slotsUsed, Selection: this.selection, Expanded: this.expanded, Exported: this.exported,
        }) : null;
    }

    /** "Nomination & feedback" / "Hide details" (logic.js:368). */
    Toggle(card: OEPCommNomineeVM): void {
        const key = Up(card.PresentationID);
        if (this.expanded.has(key)) this.expanded.delete(key);
        else this.expanded.add(key);
        this.rebuild();
        this.Changed();
    }

    /**
     * Pick or unpick a nominee (logic.js:370-376). A pick creates the section's slate on first use, then
     * a Committee Selection Items row (Slot 1; the server assigns the real slot); an unpick deletes it.
     */
    async Pick(card: OEPCommNomineeVM): Promise<void> {
        const season = this.Context.Season;
        const seasonID = this.seasonID;
        const sectionID = this.sectionID;
        if (!season || seasonID === null || sectionID === null || this.Busy || !card.Pick.Enabled) return;
        if (!CanEditSlate(season.Phase, this.selection)) return;
        this.Status = null;
        const itemID = card.ItemID;
        const pid = card.PresentationID;
        // True only while the slate row is being created (assigned inside the Act closure, which TS flow analysis cannot see).
        let startingSlate = false as boolean;
        const ok = await this.Act(async () => {
            if (itemID !== null) {
                await this.Data.Delete(OEP_ENTITY.CommitteeSelectionItems, itemID);
                return;
            }
            let selectionID = this.selection?.ID ?? null;
            if (selectionID === null) {
                startingSlate = true;
                const created = await this.Data.SaveNew<FeedbackPlatformCommitteeSelectionEntity>(OEP_ENTITY.CommitteeSelections, (s) => {
                    s.SeasonID = seasonID; s.SectionID = sectionID; s.Status = 'Draft';
                });
                startingSlate = false;
                selectionID = created.ID;
                this.selection = { ID: created.ID, Status: 'Draft', SubmittedAt: null }; // a failed item insert reuses it
            }
            const slate = selectionID;
            await this.Data.SaveNew<FeedbackPlatformCommitteeSelectionItemEntity>(OEP_ENTITY.CommitteeSelectionItems, (i) => {
                i.CommitteeSelectionID = slate; i.PresentationID = pid; i.Slot = 1;
            });
        }, null, (m) => { this.Status = { Text: m, Tone: 'error' }; });
        // A refusal (e.g. another member took the last slot — UQ_CommitteeSelectionItem_Selection_Slot) reloads the slate as the server has it.
        if (!ok) {
            await this.Refresh();
            // Lost the first-pick race: another member's slate row won UQ_CommitteeSelection_Season_Section.
            if (startingSlate && this.selection !== null) this.Status = { Text: OEP_SLATE_RACE, Tone: 'error' };
        }
        this.Changed();
    }

    /** "Submit selections" → MJConfirmService.Confirm → Status Submitted, stamped with me (DecideSelectionChange). */
    async Submit(): Promise<void> {
        const season = this.Context.Season;
        const open = this.VM?.Open;
        const sel = this.selection;
        if (!season || !open || sel === null || this.Busy || !CanSubmitSlate(season.Phase, sel, open.PickedCount)) return;
        const n = open.PickedCount;
        const yes = await this.confirm.Confirm({
            title: 'Submit your section\'s winners?',
            message: `${n} ${n === 1 ? 'winner' : 'winners'} for ${this.sectionName}.`,
            detail: 'AGU staff see your section as complete once you submit. Contact the OEP team if your committee needs to change its slate.',
            confirmText: 'Submit selections',
            cancelText: 'Keep editing',
        });
        if (!yes) return;
        this.Status = null;
        const me = this.Context.Me.UserID;
        const ok = await this.Act(async () => {
            await this.Data.Update<FeedbackPlatformCommitteeSelectionEntity>(OEP_ENTITY.CommitteeSelections, sel.ID, (s) => {
                s.Status = 'Submitted'; s.SubmittedAt = new Date(); s.SubmittedByUserID = me;
            });
        }, null, (m) => { this.Status = { Text: m, Tone: 'error' }; });
        if (ok) this.Status = { Text: 'Selections submitted to AGU', Tone: 'ok' };
        else await this.Refresh();
        this.Changed();
    }

    /** "Download all scores (.xlsx)" (logic.js:721-722) → ExportEngine.toExcel, downloaded as a Blob. */
    async Export(): Promise<void> {
        if (this.Busy) return;
        this.Status = null;
        try {
            await DownloadScores(BuildScoreRows(this.nominees), ScoreFileName(this.seasonCode, this.sectionName));
            this.exported = true;
            this.rebuild();
        } catch (err) {
            this.Status = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }

    /** "View the final presentation": the tab opens inside the click (OEPOpenInNewTab), then gets the signed URL. */
    async ViewFinal(card: OEPCommNomineeVM): Promise<void> {
        const fileID = card.FinalFileID;
        if (fileID === null) return;
        this.Status = null;
        try {
            const tab = await OEPOpenInNewTab(() => this.files.DownloadUrl(fileID));
            if (tab.Blocked) this.Status = { Text: OEP_TAB_BLOCKED, Tone: 'error', Link: { Label: 'Open the file', Url: tab.Url } };
        } catch (err) {
            this.Status = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }
}
